import { useState, useEffect } from"react";
import axios from"axios";
import { useTranslation } from"react-i18next";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Badge } from"@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from"@/components/ui/dialog";
import { Textarea } from"@/components/ui/textarea";
import { Label } from"@/components/ui/label";
import { 
 Receipt, 
 CheckCircle, 
 XCircle, 
 Clock, 
 DollarSign, 
 FileText,
 TrendingUp,
 AlertTriangle,
 HardHat,
 Download
} from"lucide-react";
import { toast } from"sonner";

const AccountantDashboard = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [loading, setLoading] = useState(true);
 const [pendingInvoices, setPendingInvoices] = useState([]);
 const [allInvoices, setAllInvoices] = useState([]);
 const [stats, setStats] = useState({
 pendingCount: 0,
 approvedToday: 0,
 totalPending: 0,
 totalApproved: 0
 });
 const [selectedInvoice, setSelectedInvoice] = useState(null);
 const [reviewNotes, setReviewNotes] = useState("");
 const [processing, setProcessing] = useState(false);

 useEffect(() => {
 loadData();
 }, []);

 const loadData = async () => {
 setLoading(true);
 try {
 // Get pending contractor invoices for accountant
 const invoicesRes = await axios.get("/contractor-invoices/pending");
 setPendingInvoices(invoicesRes.data.filter(inv => inv.status ==="pending_accountant"));
 setAllInvoices(invoicesRes.data);
 
 // Calculate stats
 const pending = invoicesRes.data.filter(inv => inv.status ==="pending_accountant");
 const approved = invoicesRes.data.filter(inv => inv.status ==="approved");
 
 setStats({
 pendingCount: pending.length,
 approvedToday: approved.filter(inv => {
 const today = new Date().toDateString();
 return new Date(inv.accountant_approved_at).toDateString() === today;
 }).length,
 totalPending: invoicesRes.data.filter(inv => 
 inv.status ==="pending_engineer"|| inv.status ==="pending_accountant"
 ).length,
 totalApproved: approved.length
 });
 } catch (error) {
 toast.error(t('messages.loadFailed'));
 } finally {
 setLoading(false);
 }
 };

 const handleApprove = async (invoiceId) => {
 setProcessing(true);
 try {
 await axios.patch(`/contractor-invoices/${invoiceId}/approve`, { 
 action:"approve",
 notes: reviewNotes 
 });
 toast.success(t('accountant.invoiceApproved'));
 setSelectedInvoice(null);
 setReviewNotes("");
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail || t('messages.saveFailed'));
 } finally {
 setProcessing(false);
 }
 };

 const handleReject = async (invoiceId) => {
 if (!reviewNotes.trim()) {
 toast.error(t('accountant.provideRejectionReason'));
 return;
 }
 setProcessing(true);
 try {
 await axios.patch(`/contractor-invoices/${invoiceId}/approve`, { 
 action:"reject",
 notes: reviewNotes 
 });
 toast.success(t('accountant.invoiceRejected'));
 setSelectedInvoice(null);
 setReviewNotes("");
 loadData();
 } catch (error) {
 toast.error(error.response?.data?.detail || t('messages.saveFailed'));
 } finally {
 setProcessing(false);
 }
 };

 const getStatusColor = (status) => {
 switch (status) {
 case"approved":
 return"bg-emerald-500/10 text-emerald-700 border-green-200/60";
 case"rejected":
 return"bg-rose-500/10 text-rose-700 border-red-200/60";
 case"pending_accountant":
 return"bg-blue-500/10 text-blue-700 border-blue-200/60";
 case"pending_engineer":
 return"bg-amber-500/10 text-amber-700 border-yellow-200/60";
 default:
 return"bg-white/60 text-slate-800 border-slate-200";
 }
 };

 const getStatusLabel = (status) => {
 switch (status) {
 case"pending_engineer":
 return t('accountant.awaitingEngineer');
 case"pending_accountant":
 return t('accountant.awaitingYourReview');
 case"approved":
 return t('invoices.approved');
 case"rejected":
 return t('invoices.rejected');
 default:
 return status;
 }
 };

 if (loading) {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-64">
 <div className="text-sm text-slate-500">{t('common.loading')}</div>
 </div>
 </Layout>
 );
 }

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="space-y-6">
 {/* Header */}
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">
 {t('accountant.title')}
 </h1>
 <p className="text-slate-600 mt-1">
 {t('accountant.subtitle')}
 </p>
 </div>

 {/* Stats Cards */}
 <div className="grid grid-cols-4 gap-4">
 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-500 font-medium">{t('accountant.pendingYourReview')}</p>
 <p className="text-2xl font-semibold tabular-nums text-slate-900">{stats.pendingCount}</p>
 </div>
 <Clock className="h-7 w-7 text-blue-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-500 font-medium">{t('accountant.awaitingEngineer')}</p>
 <p className="text-2xl font-semibold tabular-nums text-slate-900">
 {allInvoices.filter(inv => inv.status ==="pending_engineer").length}
 </p>
 </div>
 <AlertTriangle className="h-7 w-7 text-amber-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-500 font-medium">{t('accountant.approvedToday')}</p>
 <p className="text-2xl font-semibold tabular-nums text-slate-900">{stats.approvedToday}</p>
 </div>
 <CheckCircle className="h-7 w-7 text-emerald-600"/>
 </div>
 </CardContent>
 </Card>

 <Card className="">
 <CardContent className="pt-6">
 <div className="flex items-center justify-between">
 <div>
 <p className="text-sm text-slate-600 font-medium">{t('accountant.totalApproved')}</p>
 <p className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">{stats.totalApproved}</p>
 </div>
 <TrendingUp className="w-10 h-10 text-slate-400"/>
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Pending Invoices for Review */}
 <Card className="">
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <Receipt className="w-5 h-5 text-blue-600"/>
 {t('accountant.invoicesPendingApproval')}
 {pendingInvoices.length > 0 && (
 <Badge variant="destructive"className="ml-2">{pendingInvoices.length}</Badge>
 )}
 </CardTitle>
 </CardHeader>
 <CardContent>
 {pendingInvoices.length === 0 ? (
 <div className="text-center py-12">
 <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3"/>
 <p className="text-slate-600">{t('accountant.noInvoicesPending')}</p>
 <p className="text-sm text-slate-500 mt-1">
 {allInvoices.filter(inv => inv.status ==="pending_engineer").length > 0 
 ? t('accountant.awaitingEngineerCount', { count: allInvoices.filter(inv => inv.status ==="pending_engineer").length })
 : t('accountant.allCaughtUp')}
 </p>
 </div>
 ) : (
 <div className="space-y-3">
 {pendingInvoices.map((invoice) => (
 <div 
 key={invoice.id} 
 className="p-4 border border-slate-200 bg-white rounded-sm hover:shadow-md transition-shadow cursor-pointer"
 onClick={() => setSelectedInvoice(invoice)}
 >
 <div className="flex items-start justify-between">
 <div className="flex items-start gap-4">
 <HardHat className="w-6 h-6 text-blue-600"/>
 <div>
 <h3 className="font-semibold text-slate-900">{invoice.invoice_number}</h3>
 <p className="text-sm text-slate-600 mt-1">
 {invoice.description || t('accountant.contractorInvoice')}
 </p>
 <div className="flex items-center gap-4 mt-2 text-[11px] text-slate-500">
 <span>{t('tableHeaders.submitted')}: {new Date(invoice.created_at).toLocaleDateString()}</span>
 {invoice.engineer_approved && (
 <span className="flex items-center gap-1 text-green-600">
 <CheckCircle className="w-3 h-3"/>
 {t('invoices.engineerApproved')}
 </span>
 )}
 </div>
 </div>
 </div>
 <div className="text-right">
 <p className="text-2xl font-bold text-slate-900">
 €{invoice.amount?.toLocaleString()}
 </p>
 <Badge variant="outline"className={getStatusColor(invoice.status)}>
 {getStatusLabel(invoice.status)}
 </Badge>
 </div>
 </div>
 </div>
 ))}
 </div>
 )}
 </CardContent>
 </Card>

 {/* All Invoices Overview */}
 <Card className="">
 <CardHeader>
 <CardTitle className="flex items-center gap-2">
 <FileText className="w-5 h-5 text-indigo-600"/>
 {t('accountant.allContractorInvoices')}
 </CardTitle>
 </CardHeader>
 <CardContent>
 {allInvoices.length === 0 ? (
 <div className="text-center py-8">
 <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3"/>
 <p className="text-slate-600">{t('accountant.noContractorInvoices')}</p>
 </div>
 ) : (
 <div className="overflow-x-auto">
 <table className="w-full">
 <thead>
 <tr className="border-b border-slate-200">
 <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">{t('tableHeaders.invoiceNumber')}</th>
 <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">{t('tableHeaders.description')}</th>
 <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">{t('tableHeaders.amount')}</th>
 <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">{t('tableHeaders.status')}</th>
 <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">{t('tableHeaders.submitted')}</th>
 <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">{t('tableHeaders.actions')}</th>
 </tr>
 </thead>
 <tbody>
 {allInvoices.map((invoice) => (
 <tr key={invoice.id} className="border-b border-white/40 hover:bg-white/80">
 <td className="py-3 px-4 text-sm font-medium text-slate-900">
 {invoice.invoice_number}
 </td>
 <td className="py-3 px-4 text-sm text-slate-600">
 {invoice.description ||"-"}
 </td>
 <td className="py-3 px-4 text-sm font-semibold text-slate-900">
 €{invoice.amount?.toLocaleString()}
 </td>
 <td className="py-3 px-4">
 <Badge variant="outline"className={getStatusColor(invoice.status)}>
 {getStatusLabel(invoice.status)}
 </Badge>
 </td>
 <td className="py-3 px-4 text-sm text-slate-600">
 {new Date(invoice.created_at).toLocaleDateString()}
 </td>
 <td className="py-3 px-4">
 {invoice.status ==="pending_accountant"&& (
 <Button 
 size="sm"
 variant="outline"
 onClick={() => setSelectedInvoice(invoice)}
 >
 {t('accountant.review')}
 </Button>
 )}
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </CardContent>
 </Card>

 {/* Review Dialog */}
 <Dialog open={!!selectedInvoice} onOpenChange={() => {
 setSelectedInvoice(null);
 setReviewNotes("");
 }}>
 <DialogContent className="max-w-xl">
 <DialogHeader>
 <DialogTitle className="font-display uppercase tracking-tight text-slate-900">{t('accountant.reviewInvoice')}</DialogTitle>
 </DialogHeader>
 {selectedInvoice && (
 <div className="space-y-4">

 {/* Invoice header */}
 <div className="flex items-center justify-between">
 <div>
 <p className="font-mono font-bold text-slate-900">{selectedInvoice.invoice_number}</p>
 <p className="text-xs text-slate-500">{selectedInvoice.partner_name}</p>
 </div>
 {selectedInvoice.engineer_approved && (
 <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium">
 <CheckCircle className="w-3.5 h-3.5"/> Engineer approved
 </span>
 )}
 </div>

 {/* Work Invoiced — the progress report details */}
 {(selectedInvoice.linked_report_title || selectedInvoice.milestone_label) && (
 <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 space-y-1">
 <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Work Invoiced</p>
 <p className="font-semibold text-slate-900">{selectedInvoice.linked_report_title || selectedInvoice.milestone_label}</p>
 {selectedInvoice.m2_completed && (
 <p className="text-sm text-slate-600">
 <span className="font-medium">{selectedInvoice.m2_completed} m²</span>
 {selectedInvoice.price_per_m2_used &&
 <span className="text-emerald-600"> × €{selectedInvoice.price_per_m2_used}/m² = <strong>€{(selectedInvoice.m2_completed * selectedInvoice.price_per_m2_used).toLocaleString()}</strong></span>
 }
 </p>
 )}
 </div>
 )}

 {/* Financial breakdown */}
 <div className="grid grid-cols-3 gap-3">
 <div className="bg-white border border-slate-200 rounded-sm p-3 text-center">
 <p className="text-[10px] text-slate-400 uppercase tracking-wider">Amount</p>
 <p className="font-bold text-slate-900 text-lg">€{(selectedInvoice.amount||0).toLocaleString()}</p>
 </div>
 <div className="bg-white border border-slate-200 rounded-sm p-3 text-center">
 <p className="text-[10px] text-slate-400 uppercase tracking-wider">Tax</p>
 <p className="font-bold text-slate-900 text-lg">€{(selectedInvoice.tax_amount||0).toLocaleString()}</p>
 </div>
 <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-3 text-center">
 <p className="text-[10px] text-indigo-600 uppercase tracking-wider font-semibold">Total</p>
 <p className="font-bold text-indigo-900 text-lg">€{(selectedInvoice.total_amount||0).toLocaleString()}</p>
 </div>
 </div>

 {/* Meta */}
 <div className="flex gap-4 text-xs text-slate-500">
 <span>Submitted: {new Date(selectedInvoice.created_at).toLocaleDateString()}</span>
 {selectedInvoice.due_date && <span>Due: {new Date(selectedInvoice.due_date).toLocaleDateString('en-GB', {day:'numeric',month:'short',year:'numeric'})}</span>}
 </div>

 {/* Optional notes */}
 {selectedInvoice.description && (
 <p className="text-xs text-slate-500 italic border-t border-slate-100 pt-2">Notes from contractor: {selectedInvoice.description}</p>
 )}

 {/* Download invoice document */}
 {selectedInvoice.invoice_attachments?.length > 0 && (
 <a
 href={selectedInvoice.invoice_attachments[0]}
 target="_blank"
 rel="noopener noreferrer"
 className="flex items-center justify-center gap-2 w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-sm py-2.5 text-sm font-medium transition-colors"
 >
 <Download className="w-4 h-4"/> Download Invoice Document
 </a>
 )}

 {/* Review Notes */}
 <div>
 <Label htmlFor="notes" className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{t('accountant.reviewNotes')}</Label>
 <Textarea
 id="notes"
 value={reviewNotes}
 onChange={(e) => setReviewNotes(e.target.value)}
 placeholder={t('accountant.addNotes')}
 rows={2}
 className="mt-1 rounded-sm text-sm"
 />
 </div>

 {/* Action Buttons */}
 <div className="flex gap-3 justify-end pt-2 border-t border-slate-100">
 <Button variant="outline" className="rounded-sm"
 onClick={() => { setSelectedInvoice(null); setReviewNotes(""); }}>
 {t('common.cancel')}
 </Button>
 <Button variant="outline" className="rounded-sm hover:bg-red-50 hover:text-red-700 hover:border-red-300"
 onClick={() => handleReject(selectedInvoice.id)} disabled={processing}>
 <XCircle className="w-4 h-4 mr-1"/>{t('common.reject')}
 </Button>
 <Button className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
 onClick={() => handleApprove(selectedInvoice.id)} disabled={processing}>
 <CheckCircle className="w-4 h-4 mr-1"/>{processing ? t('accountant.processing') : t('common.approve')}
 </Button>
 </div>
 </div>
 )}
 </DialogContent>
 </Dialog>
 </div>
 </Layout>
 );
};

export default AccountantDashboard;
